import { useState, useCallback } from 'react';
import { cases, type CaseItem } from '../../../mocks/cases';

// ── 对比维度定义 ──
interface CompareDimension {
  key: keyof CaseItem | 'highlights_count' | 'has_contract';
  label: string;
  icon: string;
  render: (c: CaseItem) => React.ReactNode;
}

const DIMENSIONS: CompareDimension[] = [
  {
    key: 'type',
    label: '资产类型',
    icon: 'ri-price-tag-3-line',
    render: (c) => (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
        {c.type}
      </span>
    ),
  },
  {
    key: 'region',
    label: '发行地区',
    icon: 'ri-map-pin-line',
    render: (c) => (
      <span className="flex items-center gap-1 text-sm text-gray-700">
        <i className="ri-map-pin-2-line text-emerald-500 text-xs"></i>
        {c.region}
      </span>
    ),
  },
  {
    key: 'platform',
    label: '区块链平台',
    icon: 'ri-links-line',
    render: (c) => (
      <span className="text-sm text-gray-700 font-medium">{c.platform}</span>
    ),
  },
  {
    key: 'tvl',
    label: '规模 / TVL',
    icon: 'ri-bar-chart-2-line',
    render: (c) => (
      <span className="text-sm font-black text-[#7C3AED]">{c.tvl}</span>
    ),
  },
  {
    key: 'status',
    label: '运营状态',
    icon: 'ri-checkbox-circle-line',
    render: (c) => {
      const colors: Record<string, string> = {
        运营中: 'bg-emerald-100 text-emerald-700',
        试点: 'bg-violet-100 text-violet-700',
        已发行: 'bg-amber-100 text-amber-700',
        规划中: 'bg-slate-100 text-slate-600',
      };
      return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${colors[c.status] ?? 'bg-gray-100 text-gray-600'}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"></span>
          {c.status}
        </span>
      );
    },
  },
  {
    key: 'track',
    label: '赛道定位',
    icon: 'ri-route-line',
    render: (c) => (
      <span className="text-sm text-gray-700">{c.track}</span>
    ),
  },
  {
    key: 'issuer',
    label: '发行机构',
    icon: 'ri-building-2-line',
    render: (c) => (
      <span className="text-sm text-gray-700">{c.issuer ?? '—'}</span>
    ),
  },
  {
    key: 'standard',
    label: '技术标准',
    icon: 'ri-code-s-slash-line',
    render: (c) => (
      <span className="text-xs text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded-lg">{c.standard ?? '—'}</span>
    ),
  },
  {
    key: 'launchDate',
    label: '发行时间',
    icon: 'ri-calendar-line',
    render: (c) => (
      <span className="text-sm text-gray-600">{c.launchDate ?? '—'}</span>
    ),
  },
  {
    key: 'has_contract',
    label: '含合约源码',
    icon: 'ri-file-code-line',
    render: (c) => (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${c.contractCode ? 'text-emerald-600' : 'text-gray-400'}`}>
        <i className={c.contractCode ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'}></i>
        {c.contractCode ? '有源码' : '无源码'}
      </span>
    ),
  },
  {
    key: 'highlights_count',
    label: '核心亮点',
    icon: 'ri-star-line',
    render: (c) => (
      <div className="space-y-1">
        {(c.highlights ?? []).slice(0, 3).map((h, i) => (
          <div key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
            <i className="ri-checkbox-blank-circle-fill text-[#7C3AED] text-[6px] mt-1.5 shrink-0"></i>
            <span className="leading-relaxed">{h}</span>
          </div>
        ))}
        {(c.highlights?.length ?? 0) > 3 && (
          <span className="text-xs text-gray-400">+{(c.highlights?.length ?? 0) - 3} 条</span>
        )}
      </div>
    ),
  },
];

// ── 导出报告生成函数 ──
function generateTextReport(activeCases: CaseItem[]): string {
  const now = new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  const separator = '═'.repeat(60);
  const divider = '─'.repeat(60);

  let report = `${separator}\n`;
  report += `  RWA99 案例横向对比报告\n`;
  report += `  生成时间：${now}\n`;
  report += `  来源：RWA99.cn · 仅供参考，不构成投资建议\n`;
  report += `${separator}\n\n`;

  report += `【对比概览】\n`;
  report += `本次对比案例数：${activeCases.length} 个\n`;
  report += `对比维度：资产类型、发行地区、区块链平台、规模/TVL、运营状态、赛道定位、发行机构、技术标准、发行时间\n\n`;

  activeCases.forEach((c, i) => {
    report += `${divider}\n`;
    report += `案例 ${i + 1}：${c.name}\n`;
    report += `${divider}\n`;
    report += `  资产类型：${c.type}\n`;
    report += `  发行地区：${c.region}\n`;
    report += `  区块链平台：${c.platform}\n`;
    report += `  规模/TVL：${c.tvl}\n`;
    report += `  运营状态：${c.status}\n`;
    report += `  赛道定位：${c.track}\n`;
    report += `  发行机构：${c.issuer ?? '—'}\n`;
    report += `  技术标准：${c.standard ?? '—'}\n`;
    report += `  发行时间：${c.launchDate ?? '—'}\n`;
    report += `  含合约源码：${c.contractCode ? '是' : '否'}\n`;
    if (c.highlights && c.highlights.length > 0) {
      report += `  核心亮点：\n`;
      c.highlights.slice(0, 5).forEach((h) => {
        report += `    · ${h}\n`;
      });
    }
    report += '\n';
  });

  // 横向对比摘要
  report += `${separator}\n`;
  report += `【横向对比摘要】\n`;
  report += `${separator}\n\n`;

  // TVL对比
  const tvlValues = activeCases.map((c) => ({ name: c.name, tvl: c.tvl }));
  report += `规模对比：\n`;
  tvlValues.forEach((v) => {
    report += `  ${v.name}：${v.tvl}\n`;
  });
  report += '\n';

  // 地区分布
  const regions = [...new Set(activeCases.map((c) => c.region))];
  report += `地区分布：${regions.join('、')}\n\n`;

  // 技术标准
  const standards = activeCases.filter((c) => c.standard).map((c) => `${c.name}（${c.standard}）`);
  if (standards.length > 0) {
    report += `技术标准：${standards.join('；')}\n\n`;
  }

  // 状态分布
  const statusMap: Record<string, string[]> = {};
  activeCases.forEach((c) => {
    if (!statusMap[c.status]) statusMap[c.status] = [];
    statusMap[c.status].push(c.name);
  });
  report += `运营状态分布：\n`;
  Object.entries(statusMap).forEach(([status, names]) => {
    report += `  ${status}：${names.join('、')}\n`;
  });
  report += '\n';

  report += `${separator}\n`;
  report += `免责声明：以上数据来源于公开信息，仅供参考学习。\n`;
  report += `投资决策请以官方文件为准，本站不构成任何投资建议。\n`;
  report += `RWA99.cn · 2026 RWA合规知识门户\n`;
  report += `${separator}\n`;

  return report;
}

// ── 案例选择器 ──
interface CaseSelectorProps {
  selected: CaseItem | null;
  onSelect: (c: CaseItem | null) => void;
  excludeIds: number[];
  slotIndex: number;
}

function CaseSelector({ selected, onSelect, excludeIds, slotIndex }: CaseSelectorProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = cases.filter((c) => {
    if (excludeIds.includes(c.id)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.type.includes(search) || c.region.includes(search);
  });

  const SLOT_COLORS = [
    { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', accent: '#7C3AED' },
    { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', accent: '#059669' },
    { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', accent: '#D97706' },
    { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', accent: '#E11D48' },
  ];
  const color = SLOT_COLORS[slotIndex] ?? SLOT_COLORS[0];

  if (selected) {
    return (
      <div className={`relative rounded-xl border-2 ${color.border} ${color.bg} p-3`}>
        <button
          onClick={() => onSelect(null)}
          className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-400 hover:text-gray-600 cursor-pointer transition-colors z-10"
        >
          <i className="ri-close-line text-xs"></i>
        </button>
        <div className="pr-6">
          <div className={`text-xs font-bold ${color.text} mb-1`}>案例 {slotIndex + 1}</div>
          <div className="font-bold text-gray-800 text-sm leading-snug line-clamp-2">{selected.name}</div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color.bg} ${color.text} border ${color.border}`}>{selected.type}</span>
            <span className="text-xs text-gray-500">{selected.region}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full rounded-xl border-2 border-dashed ${color.border} ${color.bg} p-4 flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity`}
      >
        <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-white/60 ${color.text}`}>
          <i className="ri-add-line text-xl"></i>
        </div>
        <span className={`text-sm font-semibold ${color.text}`}>选择案例 {slotIndex + 1}</span>
        <span className="text-xs text-gray-400">点击从87个案例中选择</span>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden">
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索案例名称、类型、地区..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 pl-8 text-sm focus:outline-none focus:border-[#7C3AED] transition-colors"
                autoFocus
              />
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.slice(0, 30).map((c) => (
              <button
                key={c.id}
                onClick={() => { onSelect(c); setOpen(false); setSearch(''); }}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
              >
                <div className="font-medium text-gray-800 text-sm line-clamp-1">{c.name}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-500">{c.type}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs text-gray-500">{c.region}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs font-semibold text-[#7C3AED]">{c.tvl}</span>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="py-8 text-center text-gray-400 text-sm">未找到匹配案例</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── 主对比弹窗 ──
interface CaseCompareProps {
  onClose: () => void;
  initialCases?: CaseItem[];
}

export default function CaseCompare({ onClose, initialCases = [] }: CaseCompareProps) {
  const [selected, setSelected] = useState<(CaseItem | null)[]>(() => {
    const arr: (CaseItem | null)[] = [null, null, null];
    initialCases.slice(0, 3).forEach((c, i) => { arr[i] = c; });
    return arr;
  });
  const [showExport, setShowExport] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSelect = useCallback((index: number, c: CaseItem | null) => {
    setSelected((prev) => {
      const next = [...prev];
      next[index] = c;
      return next;
    });
  }, []);

  const activeCases = selected.filter(Boolean) as CaseItem[];
  const excludeIds = activeCases.map((c) => c.id);

  // 高亮最大TVL
  const tvlValues = activeCases.map((c) => {
    const num = parseFloat(c.tvl.replace(/[^0-9.]/g, ''));
    return isNaN(num) ? 0 : num;
  });
  const maxTvlIdx = tvlValues.indexOf(Math.max(...tvlValues));

  const reportText = activeCases.length >= 2 ? generateTextReport(activeCases) : '';

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(reportText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [reportText]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RWA99_案例对比报告_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [reportText]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-6 px-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#2E1065] to-[#4C1D95]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-scales-3-line text-white text-lg"></i>
            </div>
            <div>
              <div className="text-white font-black text-lg">案例横向对比</div>
              <div className="text-white/60 text-xs">最多同时对比3个案例 · 11个关键维度</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeCases.length >= 2 && (
              <button
                onClick={() => setShowExport(!showExport)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  showExport
                    ? 'bg-white text-[#7C3AED]'
                    : 'bg-white/15 text-white hover:bg-white/25'
                }`}
              >
                <i className="ri-download-2-line text-sm"></i>
                导出报告
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-white"></i>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* 案例选择区 */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[0, 1, 2].map((i) => (
              <CaseSelector
                key={i}
                selected={selected[i]}
                onSelect={(c) => handleSelect(i, c)}
                excludeIds={excludeIds.filter((_, idx) => idx !== i || !selected[i])}
                slotIndex={i}
              />
            ))}
          </div>

          {/* 导出报告面板 */}
          {showExport && activeCases.length >= 2 && (
            <div className="mb-6 border border-[#7C3AED]/20 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 bg-[#7C3AED]/5 border-b border-[#7C3AED]/10">
                <div className="flex items-center gap-2">
                  <i className="ri-file-text-line text-[#7C3AED]"></i>
                  <span className="text-sm font-bold text-[#7C3AED]">对比报告预览</span>
                  <span className="text-xs text-gray-400">· 文本格式 · 可复制或下载</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all whitespace-nowrap ${
                      copied
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-[#7C3AED] hover:text-[#7C3AED]'
                    }`}
                  >
                    <i className={copied ? 'ri-checkbox-circle-line' : 'ri-clipboard-line'}></i>
                    {copied ? '已复制！' : '复制文本'}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#7C3AED] text-white hover:bg-[#6D28D9] cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-download-2-line"></i>
                    下载 .txt
                  </button>
                </div>
              </div>
              <div className="p-4 bg-gray-50 max-h-64 overflow-y-auto">
                <pre className="text-xs text-gray-600 font-mono leading-relaxed whitespace-pre-wrap break-words">{reportText}</pre>
              </div>
            </div>
          )}

          {activeCases.length < 2 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                <i className="ri-scales-3-line text-3xl text-gray-300"></i>
              </div>
              <p className="text-base font-medium text-gray-500">请至少选择 2 个案例开始对比</p>
              <p className="text-sm text-gray-400 mt-1">从上方选择框中选择你想对比的案例</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide w-32 bg-gray-50 rounded-tl-xl">维度</th>
                    {activeCases.map((c, i) => {
                      const HEADER_COLORS = [
                        'from-violet-600 to-violet-500',
                        'from-emerald-600 to-emerald-500',
                        'from-amber-600 to-amber-500',
                      ];
                      return (
                        <th key={c.id} className={`py-3 px-4 text-left bg-gradient-to-r ${HEADER_COLORS[i]} text-white`}>
                          <div className="font-bold text-sm line-clamp-2 leading-snug">{c.name}</div>
                          <div className="text-white/70 text-xs mt-0.5">{c.type} · {c.region}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {DIMENSIONS.map((dim, dimIdx) => (
                    <tr key={dim.key} className={dimIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                      <td className="py-3 px-4 border-r border-gray-100">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#7C3AED]/10">
                            <i className={`${dim.icon} text-[#7C3AED] text-xs`}></i>
                          </div>
                          <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">{dim.label}</span>
                        </div>
                      </td>
                      {activeCases.map((c, cIdx) => {
                        const isBest = dim.key === 'tvl' && cIdx === maxTvlIdx && activeCases.length > 1;
                        return (
                          <td
                            key={c.id}
                            className={`py-3 px-4 border-r border-gray-100 last:border-0 ${isBest ? 'bg-violet-50/60' : ''}`}
                          >
                            {isBest && (
                              <div className="flex items-center gap-1 mb-1">
                                <i className="ri-trophy-line text-amber-500 text-xs"></i>
                                <span className="text-xs text-amber-600 font-semibold">规模最大</span>
                              </div>
                            )}
                            {dim.render(c)}
                          </td>
                        );
                      })}
                      {/* 空列占位 */}
                      {Array.from({ length: 3 - activeCases.length }).map((_, i) => (
                        <td key={`empty-${i}`} className="py-3 px-4 border-r border-gray-100 last:border-0">
                          <span className="text-gray-200 text-sm">—</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 底部说明 */}
          {activeCases.length >= 2 && (
            <div className="mt-4 flex items-center justify-between gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <i className="ri-information-line text-gray-400"></i>
                <span>对比数据来源于公开信息，仅供参考。投资决策请以官方文件为准。</span>
              </div>
              <button
                onClick={() => setShowExport(true)}
                className="flex items-center gap-1 text-[#7C3AED] font-semibold hover:text-[#6D28D9] cursor-pointer whitespace-nowrap transition-colors shrink-0"
              >
                <i className="ri-download-2-line text-sm"></i>
                导出报告
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
